import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Timer, Save, Play, RefreshCw, Plus, Trash2, X, Eye, Users, Building2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter 143 — Configuración → Recordatorios.
 * Multi-recordatorio (A, B, C…) con plantillas HTML5, editor y previsualización.
 */

// Ejemplo de contexto para renderizar el preview HTML.
const PREVIEW_CTX = {
  nombre_cliente: "María García",
  numero_reparacion: "REP-1234",
  equipo: "MacBook Pro",
  modelo: "A2338",
  numero_serie: "SN-XYZ-9876",
  dias_en_reparacion: "5",
  tecnico: "Carlos",
  fecha_entrada_en_reparacion: "01/09/2026 10:30",
  fecha_entrega: "",
  numero_factura: "",
};

const renderTemplate = (t, ctx) => {
  let out = t || "";
  Object.entries(ctx).forEach(([k, v]) => {
    out = out.replaceAll(`{${k}}`, String(v ?? ""));
  });
  return out;
};

const DEFAULT_HTML = `<div style="font-family:sans-serif;color:#111;max-width:640px;margin:0 auto;">
  <div style="border-bottom:3px solid #6E8B3D;padding:16px 0;">
    <h2 style="margin:0;">Actualización sobre tu reparación {numero_reparacion}</h2>
  </div>
  <div style="padding:20px 0;line-height:1.6;">
    <p>Hola <strong>{nombre_cliente}</strong>,</p>
    <p>Tu equipo <strong>{equipo} {modelo}</strong> continúa en proceso de reparación.</p>
    <p>Te avisaremos en cuanto tengamos novedades.</p>
    <p style="margin-top:20px;">Gracias por tu paciencia.</p>
  </div>
</div>`;

export default function ReminderSettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // reminder rule being edited
  const [previewMode, setPreviewMode] = useState("customer");   // customer|internal

  const load = async () => {
    try {
      const r = await api.get("/settings/repair-reminders");
      setCfg(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get("/settings/repair-reminders/history");
      setHistory(r.data.items || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setHistoryLoading(false); }
  };

  useEffect(() => { load(); loadHistory(); }, []);

  const upd = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const updReminders = (list) => setCfg((c) => ({ ...c, reminders: list }));

  const save = async (nextCfg = null) => {
    setSaving(true);
    try {
      const payload = nextCfg || cfg;
      const r = await api.put("/settings/repair-reminders", payload);
      setCfg(r.data);
      toast.success("Configuración guardada");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/settings/repair-reminders/run-now");
      const d = r.data;
      toast.success(
        `Comprobación · revisadas ${d.checked || 0} · cliente ${d.sent_customer || 0} · interno ${d.sent_internal || 0} · omitidas ${d.skipped || 0}`,
      );
      await loadHistory();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRunning(false); }
  };

  const addReminder = () => {
    const idx = (cfg.reminders || []).length;
    const letter = String.fromCharCode(65 + idx); // A, B, C...
    setEditing({
      id: "",
      name: `Recordatorio ${letter}`,
      days: 5 + idx * 5,
      active: true,
      send_to_customer: true,
      send_to_internal: false,
      internal_emails: "",
      subject: "Actualización sobre tu reparación {numero_reparacion}",
      body_html: DEFAULT_HTML,
      subject_internal: "Recordatorio interno · {numero_reparacion}",
      body_html_internal: "<div>{numero_reparacion} · {nombre_cliente} · {dias_en_reparacion}d</div>",
    });
  };

  const editReminder = (r) => setEditing({ ...r });

  const saveReminder = () => {
    const list = [...(cfg.reminders || [])];
    if (editing.id) {
      const i = list.findIndex((x) => x.id === editing.id);
      if (i >= 0) list[i] = editing;
      else list.push(editing);
    } else {
      list.push({ ...editing, id: crypto.randomUUID?.() || String(Math.random()) });
    }
    setEditing(null);
    save({ ...cfg, reminders: list });
  };

  const deleteReminder = (id) => {
    if (!confirm("¿Eliminar este recordatorio?")) return;
    const list = (cfg.reminders || []).filter((r) => r.id !== id);
    save({ ...cfg, reminders: list });
  };

  const previewHtml = useMemo(() => {
    if (!editing) return "";
    const html = previewMode === "customer" ? editing.body_html : editing.body_html_internal;
    return renderTemplate(html, PREVIEW_CTX);
  }, [editing, previewMode]);

  if (!cfg) return <div className="p-6">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="reminder-settings-tab">
      {/* Cabecera y configuración general */}
      <div className="border border-[#111110] bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Timer size={18} />
          <h2 className="font-display text-lg font-black">Recordatorios · EN REPARACIÓN</h2>
          {cfg.enabled && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Activo</span>}
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Envía emails HTML5 cuando una reparación lleva X días en estado
          <strong> EN REPARACIÓN</strong>. Contador desde el instante exacto de entrada al estado.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={cfg.enabled}
              onChange={(e) => upd("enabled", e.target.checked)} data-testid="reminder-enabled" />
            <span className="font-semibold">Activar recordatorios</span>
          </label>
          <label className="text-sm">
            <div className="mb-1 font-semibold">Hora ejecución (Madrid)</div>
            <div className="flex gap-2">
              <input type="number" min="0" max="23" className="w-full border px-3 py-2"
                value={cfg.run_hour}
                onChange={(e) => upd("run_hour", Math.max(0, Math.min(23, parseInt(e.target.value || "0", 10))))} />
              <span className="py-2">:</span>
              <input type="number" min="0" max="59" className="w-full border px-3 py-2"
                value={cfg.run_minute}
                onChange={(e) => upd("run_minute", Math.max(0, Math.min(59, parseInt(e.target.value || "0", 10))))} />
            </div>
          </label>
          <div className="flex items-end gap-2">
            <button onClick={() => save()} disabled={saving}
              className="inline-flex items-center gap-2 bg-[#6E8B3D] text-white px-4 py-2 text-sm font-semibold hover:bg-[#5c7534] disabled:opacity-50"
              data-testid="reminder-save-btn">
              <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
            </button>
            <button onClick={runNow} disabled={running || !cfg.enabled}
              className="inline-flex items-center gap-2 border px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              data-testid="reminder-run-now-btn">
              <Play size={14} /> {running ? "Ejecutando…" : "Ejecutar ahora"}
            </button>
          </div>
        </div>
      </div>

      {/* Lista de recordatorios A, B, C... */}
      <div className="border border-[#111110] bg-white p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base font-black">Recordatorios configurados</h3>
          <button onClick={addReminder}
            className="inline-flex items-center gap-1 bg-[#6E8B3D] text-white px-3 py-1.5 text-xs"
            data-testid="reminder-add-btn">
            <Plus size={12} /> Añadir recordatorio
          </button>
        </div>
        <table className="w-full text-xs" data-testid="reminder-list-table">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-2 py-1.5 border-b">Nombre</th>
              <th className="px-2 py-1.5 border-b">Días</th>
              <th className="px-2 py-1.5 border-b">Cliente</th>
              <th className="px-2 py-1.5 border-b">Interno</th>
              <th className="px-2 py-1.5 border-b">Activa</th>
              <th className="px-2 py-1.5 border-b">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {(cfg.reminders || []).length === 0 && (
              <tr><td colSpan={6} className="px-2 py-4 text-gray-500 text-center">
                Aún no hay recordatorios. Haz clic en "Añadir recordatorio" para crear el primero (por ejemplo, Modelo A a los 5 días).
              </td></tr>
            )}
            {(cfg.reminders || []).map((r) => (
              <tr key={r.id} className="border-b hover:bg-gray-50">
                <td className="px-2 py-1.5 font-semibold">{r.name}</td>
                <td className="px-2 py-1.5">{r.days} días</td>
                <td className="px-2 py-1.5">{r.send_to_customer ? <Users size={12} className="text-green-700" /> : <span className="text-gray-400">—</span>}</td>
                <td className="px-2 py-1.5">
                  {r.send_to_internal ? (
                    <span className="text-green-700 inline-flex items-center gap-1"><Building2 size={12} /> {r.internal_emails || <em className="text-[#B84A2A]">sin emails</em>}</span>
                  ) : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-2 py-1.5">{r.active ? <span className="text-green-700">Sí</span> : <span className="text-gray-400">No</span>}</td>
                <td className="px-2 py-1.5">
                  <button onClick={() => editReminder(r)} className="border px-2 py-0.5 mr-1 hover:bg-gray-100">Editar</button>
                  <button onClick={() => deleteReminder(r.id)} className="border px-2 py-0.5 text-[#B84A2A] hover:bg-red-50"><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal editor */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white border border-[#111110] w-full max-w-5xl max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h4 className="font-display font-black">{editing.id ? "Editar recordatorio" : "Nuevo recordatorio"}</h4>
              <button onClick={() => setEditing(null)}><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <label className="block text-sm">
                  <div className="font-semibold mb-1">Nombre</div>
                  <input type="text" className="w-full border px-3 py-2 text-sm"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    data-testid="reminder-edit-name" />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-sm">
                    <div className="font-semibold mb-1">Días desde EN REPARACIÓN</div>
                    <input type="number" min="1" className="w-full border px-3 py-2 text-sm"
                      value={editing.days}
                      onChange={(e) => setEditing({ ...editing, days: parseInt(e.target.value || "1", 10) })}
                      data-testid="reminder-edit-days" />
                  </label>
                  <label className="text-sm flex items-center gap-2 pt-6">
                    <input type="checkbox" checked={editing.active}
                      onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                      data-testid="reminder-edit-active" />
                    <span className="font-semibold">Activa</span>
                  </label>
                </div>

                {/* Destinatarios */}
                <div className="border p-3">
                  <div className="font-semibold text-xs mb-2">Destinatarios</div>
                  <label className="text-sm flex items-center gap-2 mb-1">
                    <input type="checkbox" checked={editing.send_to_customer}
                      onChange={(e) => setEditing({ ...editing, send_to_customer: e.target.checked })} />
                    <span>Enviar al cliente</span>
                  </label>
                  <label className="text-sm flex items-center gap-2 mb-2">
                    <input type="checkbox" checked={editing.send_to_internal}
                      onChange={(e) => setEditing({ ...editing, send_to_internal: e.target.checked })} />
                    <span>Enviar al equipo interno</span>
                  </label>
                  <input type="text" className="w-full border px-2 py-1.5 text-xs font-mono disabled:bg-gray-100"
                    value={editing.internal_emails}
                    disabled={!editing.send_to_internal}
                    onChange={(e) => setEditing({ ...editing, internal_emails: e.target.value })}
                    placeholder="tecnico@empresa.com, admin@empresa.com" />
                </div>

                {/* Asunto/Cuerpo cliente */}
                <div className="border p-3">
                  <div className="font-semibold text-xs mb-2">Email cliente (HTML5)</div>
                  <label className="block text-sm mb-2">
                    <div className="mb-1 text-xs">Asunto</div>
                    <input type="text" className="w-full border px-2 py-1.5 text-sm"
                      value={editing.subject}
                      onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
                  </label>
                  <div className="mb-1 text-xs">Cuerpo HTML</div>
                  <textarea className="w-full border px-2 py-1.5 text-xs font-mono" rows={10}
                    value={editing.body_html}
                    onChange={(e) => setEditing({ ...editing, body_html: e.target.value })}
                    data-testid="reminder-edit-html" />
                </div>

                {/* Asunto/Cuerpo interno */}
                <div className="border p-3">
                  <div className="font-semibold text-xs mb-2">Email interno (HTML5)</div>
                  <label className="block text-sm mb-2">
                    <div className="mb-1 text-xs">Asunto</div>
                    <input type="text" className="w-full border px-2 py-1.5 text-sm"
                      value={editing.subject_internal}
                      onChange={(e) => setEditing({ ...editing, subject_internal: e.target.value })} />
                  </label>
                  <div className="mb-1 text-xs">Cuerpo HTML</div>
                  <textarea className="w-full border px-2 py-1.5 text-xs font-mono" rows={6}
                    value={editing.body_html_internal}
                    onChange={(e) => setEditing({ ...editing, body_html_internal: e.target.value })} />
                </div>
              </div>

              {/* Preview */}
              <div className="border p-3 bg-gray-50 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-xs inline-flex items-center gap-1"><Eye size={12} /> Vista previa</div>
                  <div className="flex text-xs border">
                    <button onClick={() => setPreviewMode("customer")}
                      className={`px-2 py-0.5 ${previewMode === "customer" ? "bg-[#6E8B3D] text-white" : ""}`}>Cliente</button>
                    <button onClick={() => setPreviewMode("internal")}
                      className={`px-2 py-0.5 ${previewMode === "internal" ? "bg-[#6E8B3D] text-white" : ""}`}>Interno</button>
                  </div>
                </div>
                <p className="text-[10px] text-gray-500 mb-2">
                  Variables: <code>{"{nombre_cliente}"} {"{numero_reparacion}"} {"{equipo}"} {"{modelo}"} {"{numero_serie}"} {"{dias_en_reparacion}"} {"{fecha_entrada_en_reparacion}"} {"{tecnico}"}</code>
                </p>
                <div className="flex-1 bg-white border overflow-auto p-2" data-testid="reminder-preview">
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                </div>
              </div>
            </div>
            <div className="border-t px-4 py-3 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="border px-3 py-1.5 text-sm">Cancelar</button>
              <button onClick={saveReminder}
                className="inline-flex items-center gap-1 bg-[#6E8B3D] text-white px-3 py-1.5 text-sm"
                data-testid="reminder-edit-save">
                <Save size={14} /> Guardar recordatorio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Historial */}
      <div className="border border-[#111110] bg-white p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base font-black">Historial de envíos</h3>
          <button onClick={loadHistory}
            className="text-xs inline-flex items-center gap-1 border px-2 py-1 hover:bg-gray-50">
            <RefreshCw size={12} /> Recargar
          </button>
        </div>
        {historyLoading ? (
          <div className="text-sm text-gray-500">Cargando…</div>
        ) : history.length === 0 ? (
          <div className="text-sm text-gray-500">Sin envíos aún.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-2 py-1.5 border-b">Fecha</th>
                  <th className="px-2 py-1.5 border-b">Reparación</th>
                  <th className="px-2 py-1.5 border-b">Regla</th>
                  <th className="px-2 py-1.5 border-b">Días</th>
                  <th className="px-2 py-1.5 border-b">Cliente</th>
                  <th className="px-2 py-1.5 border-b">Interno</th>
                  <th className="px-2 py-1.5 border-b">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b hover:bg-gray-50">
                    <td className="px-2 py-1.5">{new Date(h.created_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid" })}</td>
                    <td className="px-2 py-1.5">{h.repair_code}</td>
                    <td className="px-2 py-1.5">{h.rule_name || "—"}</td>
                    <td className="px-2 py-1.5">{h.days_in_state}</td>
                    <td className="px-2 py-1.5">{h.sent_to_customer ? <span className="text-green-700">✓ {(h.customer_emails || []).join(", ")}</span> : <span className="text-gray-400">—</span>}</td>
                    <td className="px-2 py-1.5">{h.sent_to_internal ? <span className="text-green-700">✓ {(h.internal_emails || []).join(", ")}</span> : <span className="text-gray-400">—</span>}</td>
                    <td className="px-2 py-1.5">{h.ok ? <span className="text-green-700">OK</span> : <span className="text-[#B84A2A]" title={(h.errors || []).join(" · ")}>ERROR</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
